import C from "@/admin/AdminTable.module.css";

import type { FieldConfig, IdFieldConfig } from "@/admin/FieldConfig.tsx";
import { WAdminTable, type WAdminTableProps } from "@/admin/WAdminTable.tsx";
import type { Action } from "@/components/Action.tsx";
import { WTooltipButton } from "@/components/button/WToolButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WFakeCheckBox } from "@/components/forms/WFakeCheckBox.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { AdminDate, type DateLike } from "@/components/l10n/DateFormat.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { WTableBody } from "@/components/table/WTable.tsx";
import type { BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { useLastNonNull } from "@/utils/react/useLastDefined.ts";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { Children, Suspense, useMemo, type Key, type ReactNode, type Ref } from "react";
import { entries, filter, pipe } from "remeda";

export function NA() {
    return <span className="text-muted">N/A</span>;
}

export function Missing() {
    return <span className="text-muted">-</span>;
}

export function DateViewer({ value }: { value: DateLike }) {
    return <AdminDate date={value} />;
}

export function YesNo({ value }: { value: boolean }) {
    return value ? "Yes" : "No";
}

/** Main text with smaller text under it */
export function Extra({ value, children, ...props }: { value: ReactNode; children: ReactNode } & BaseProps) {
    return (
        <div {...props}>
            <div>{value ?? <Missing />}</div>
            {Children.count(children) > 0 && <div className="text-muted font-size-small">{children}</div>}
        </div>
    );
}

export function Uuid({ value }: { value: string }) {
    return <code className="max-w-9em text-ellipsis overflow-x-hidden">{value}</code>;
}

export function UuidCopyable({ value }: { value: string }) {
    return (
        <ClipBoardTooltip text={value}>
            <Uuid value={value} />
        </ClipBoardTooltip>
    );
}

function isUuid(value: string) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
}

function FieldValue<TItem, TValue>({
    field,
    value,
    item,
}: {
    field: FieldConfig<TItem, TValue>;
    value: TValue;
    item: TItem;
}) {
    const { render: Render } = field;
    let rendered = Render ? (
        <Render value={value} item={item} />
    ) : value instanceof Date ? (
        <DateViewer value={value} />
    ) : typeof value === "string" ? (
        isUuid(value) ? (
            <Uuid value={value} />
        ) : (
            // NB: Assume raw text, so display "\n" as actual newlines
            <span className="whitespace-pre max-w-350px text-ellipsis">{value}</span>
        )
    ) : typeof value === "number" ? (
        String(value)
    ) : typeof value === "boolean" ? (
        <WFakeCheckBox isSelected={value} />
    ) : value == null ? (
        <Missing />
    ) : (
        String(value)
    );
    if (field.copyable) {
        rendered = value ? (
            <ClipBoardTooltip isHtml hideIcon text={typeof value === "string" ? value : undefined}>
                {rendered}
            </ClipBoardTooltip>
        ) : (
            <Missing />
        );
    }
    return rendered;
}

interface AdminTableProps<TItem> extends Omit<WAdminTableProps, "children"> {
    items: TItem[];
    getId: (item: TItem) => Key;
    fields: Record<string, FieldConfig<TItem, any>>;
    actionButtons?: (item: TItem) => ReactNode;
    topRef?: Ref<HTMLTableSectionElement>;
}

export function AdminTable<TItem>({
    items,
    fields,
    getId,
    actionButtons,
    topRef,
    ...tableProps
}: AdminTableProps<TItem>) {
    const fs = pipe(
        fields,
        entries(),
        filter(([, field]) => field.inTable !== false),
    );

    return (
        <WAdminTable {...tableProps}>
            <thead ref={topRef}>
                <tr>
                    {fs.map(([id, field]) => (
                        <th key={id}>{field.title}</th>
                    ))}
                </tr>
            </thead>
            <WTableBody>
                {items.map((item) => (
                    <tr key={getId(item)}>
                        {fs.map(([id, field]) => {
                            const value: unknown = field.get(item);
                            return (
                                <td key={id} className={field.cellClassName}>
                                    <FieldValue field={field} value={value} item={item} />
                                </td>
                            );
                        })}
                        <td className="text-right">
                            <div className="flex gap-0.5em justify-end">{actionButtons?.(item)}</div>
                        </td>
                    </tr>
                ))}
            </WTableBody>
        </WAdminTable>
    );
}

export interface AdminPowerTableProps<
    TItem,
    TField extends IdFieldConfig<TItem, any> = IdFieldConfig<TItem, any>,
> extends Omit<WAdminTableProps, "children"> {
    fields: TField[];
    /** `null` / `undefined` means loading */
    items: TItem[] | null | undefined;

    getId: (item: TItem) => string;
    getRowClassname?: (item: TItem) => string | undefined;
    actionButtons?: (item: TItem) => ReactNode;

    renderTitle?: (field: TField) => ReactNode;
    onClickRow?: (item: TItem) => void | Promise<void>;
    rowLink?: (item: TItem) => Action;

    topRef?: Ref<HTMLTableSectionElement>;
}

const LOADING_ROW = (
    <tr>
        <td colSpan={99} className="text-left bg-grey-2">
            {/* TODO: This gets centered in the table, meaning it will generally be off-screen since the center of the table is only visible when scrolled */}
            <Loading icon="none" fillSpace />
        </td>
    </tr>
);

export function AdminPowerTable<TItem, TField extends IdFieldConfig<TItem, any>>({
    items,
    fields,
    getId,
    actionButtons,
    topRef,
    renderTitle,
    getRowClassname,
    onClickRow,
    rowLink,
    ...tableProps
}: AdminPowerTableProps<TItem, TField>) {
    //#region Hooks
    // Keep the previous items shown after starting loading, to prevent scrollbar jumping
    const cachedItems = useLastNonNull(items);

    // Cache the field cells to prevent extra re-rendering
    // Structure: `cells[rowIndex][fieldIndex]`
    const fieldCells = useMemo(
        () =>
            cachedItems?.map((item) =>
                fields.map((field) => {
                    const value: unknown = field.get(item);
                    return (
                        <td key={field.id} className={cn(field.cellClassName, field.align)}>
                            <FieldValue field={field} value={value} item={item} />
                        </td>
                    );
                }),
            ),
        [cachedItems, fields],
    );

    const actionButtonsCells = useMemo(
        () =>
            cachedItems?.map(
                (item) =>
                    actionButtons && (
                        // eslint-disable-next-line react/jsx-key
                        <td className="text-right">
                            <div className="flex pl-2 gap-0.5em justify-end">{actionButtons(item)}</div>
                        </td>
                    ),
            ),
        [cachedItems, actionButtons],
    );

    const rowLinkCells = useMemo(
        () =>
            cachedItems?.map(
                (item) =>
                    rowLink && (
                        // eslint-disable-next-line react/jsx-key
                        <td className={C.RowLink}>
                            <WTooltipButton
                                title="Open details"
                                variant="ghost"
                                size="md"
                                icon="i-lucide:external-link"
                                color="muted"
                                fastLink
                                action={rowLink(item)}
                            />
                        </td>
                    ),
            ),
        [cachedItems, rowLink],
    );

    //#endregion

    const headerCells = [
        !!rowLink && <th key="__row_action__" />,
        ...fields.map((field) => (
            <th key={field.id} className={field.align}>
                {renderTitle ? renderTitle(field) : field.title}
            </th>
        )),
        !!actionButtons && (
            <th key="__actions__">
                <MaskIcon className="ml-1 mt-1 i-lucide:ellipsis size-1rem" />
            </th>
        ),
        <th key="__spacer__" className={C.Spacer} />,
    ];

    const body =
        cachedItems?.map((item, idx) => (
            <tr
                key={getId(item)}
                className={getRowClassname?.(item)}
                onClick={
                    onClickRow
                        ? () => {
                              discardPromise(onClickRow(item));
                          }
                        : undefined
                }
            >
                {rowLinkCells![idx]}
                {fieldCells![idx]}
                {actionButtonsCells![idx]}
                {/* Spacer column that will use up any extra width, if available, to prevent other table columns from "stretching out" too much */}
                <td className={C.Spacer} />
            </tr>
        )) ?? LOADING_ROW;

    return (
        <WAdminTable {...tableProps}>
            <thead ref={topRef}>
                <tr className={cn(tableProps.sticky && C.StickyHead)}>{headerCells}</tr>
            </thead>
            <WTableBody
                // Display the loading state as "grayed out" via opacity hack
                className={cn(!items && "opacity-50%", onClickRow && "cursor-default")}
            >
                <Suspense fallback={LOADING_ROW}>{body}</Suspense>
            </WTableBody>
        </WAdminTable>
    );
}
